import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getLoginActivity, LoginEvent } from '../../../services/adminApi';
import { formatTimeAgo, formatTimestamp } from '../../../utils/dateFormatters';
import { Button, EmptyState, Spinner, StatusPill, tableStyles } from '../../ui';
import { orgLabel, orgVariant } from './loginActivityFormat';
import { loginActivityStyles as styles } from './loginActivityStyles';

const PAGE_SIZE = 50;

/** One row per fresh sign-in (Firebase auth_time), as recorded since before sessions existed. */
const SignInsView: React.FC = () => {
  const [events, setEvents] = useState<LoginEvent[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestSeqRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const loadFirstPage = useCallback(async (searchTerm: string) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getLoginActivity({
        limit: PAGE_SIZE,
        search: searchTerm || undefined,
      });
      if (seq !== requestSeqRef.current) return;
      setEvents(result.events);
      setCursor(result.nextCursor);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      setError(err?.message || 'Failed to load login activity.');
      setEvents([]);
      setCursor(null);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFirstPage(debouncedSearch);
  }, [debouncedSearch, loadFirstPage]);

  const handleLoadMore = useCallback(async () => {
    if (cursor == null || loadingMore) return;
    const seq = ++requestSeqRef.current;
    setLoadingMore(true);
    try {
      const result = await getLoginActivity({
        limit: PAGE_SIZE,
        before: cursor,
        search: debouncedSearch || undefined,
      });
      if (seq !== requestSeqRef.current) return;
      setEvents((prev) => [...prev, ...result.events]);
      setCursor(result.nextCursor);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      setError(err?.message || 'Failed to load more.');
    } finally {
      if (seq === requestSeqRef.current) setLoadingMore(false);
    }
  }, [cursor, debouncedSearch, loadingMore]);

  return (
    <>
      <div style={styles.toolbar}>
        <span />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email"
          style={styles.searchInput}
          aria-label="Search sign-ins"
        />
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {loading ? (
        <div style={styles.loadingRow}>
          <Spinner label="Loading sign-ins…" />
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          brand={false}
          maxWidth={480}
          title={debouncedSearch ? 'No matching sign-ins' : 'No sign-ins yet'}
          subtitle={
            debouncedSearch
              ? 'No sign-ins match that search. Try a different name or email.'
              : 'No sign-ins recorded yet. Rows appear here as team members sign in.'
          }
        />
      ) : (
        <>
          <div style={{ ...tableStyles.wrapScroll, marginBottom: 16 }}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>User</th>
                  <th style={tableStyles.th}>Email</th>
                  <th style={tableStyles.th}>Org</th>
                  <th style={tableStyles.th}>Signed in</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr
                    key={e.id}
                    onMouseEnter={(ev) => { ev.currentTarget.style.background = tableStyles.rowHoverBackground; }}
                    onMouseLeave={(ev) => { ev.currentTarget.style.background = ''; }}
                  >
                    <td style={tableStyles.td}>{e.displayName || '—'}</td>
                    <td style={tableStyles.td}>{e.email}</td>
                    <td style={tableStyles.td}>
                      <StatusPill variant={orgVariant(e.organization)} noDot>
                        {orgLabel(e.organization)}
                      </StatusPill>
                    </td>
                    <td style={tableStyles.td} title={formatTimestamp(e.signInAt)}>
                      {formatTimeAgo(e.signInAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {cursor != null && (
            <div style={styles.loadMoreRow}>
              <Button variant="ghost" size="sm" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SignInsView;
